/**
 * Angry Chickz Platform — Main Application
 *
 * Boot sequence, auth gate, navigation, app loading, and global state.
 * Multi-app platform with launcher home screen.
 */

import { getAppConfig, isSupabaseConfigured } from "./cloud/config.js";
import posthog from "./analytics.js";
import {
  createSupabaseBrowserClient,
  getSupabaseSession,
  signInSupabaseWithPassword,
  signOutSupabase,
  subscribeToSupabaseAuth,
  resetPasswordForEmail,
  updateUserPassword,
} from "./cloud/supabaseAuth.js";
import { renderLauncher } from "./platform/launcher.js";
import { loadApp, navigateApp, destroyApp } from "./platform/app-loader.js";
import { renderHeaderHome, renderHeaderApp } from "./platform/header.js";
import { isImpersonating, impersonationTarget, stopImpersonation } from "./platform/impersonation.js";
import { initTheme } from "./platform/theme.js";

// App manifests — imported statically so the launcher loads instantly
import lrmManifest from "./apps/lrm/manifest.js";
import grandOpeningsManifest from "./apps/grand-openings/manifest.js";
import vaultManifest from "./apps/vault/manifest.js";
import socialCalendarManifest from "./apps/social-calendar/manifest.js";
// Admin and Super tiles were merged into the Settings app. Manifests are
// kept as imports (not in ALL_APPS) so the source dirs can stay during the
// transition; remove the imports and src/apps/admin + src/apps/super-admin
// after a few days of stable use.
// import adminManifest from "./apps/admin/manifest.js";
// import superAdminManifest from "./apps/super-admin/manifest.js";
import locationsManifest from "./apps/locations/manifest.js";
import gameAdminManifest from "./apps/game-admin/manifest.js";
import settingsManifest from "./apps/settings/manifest.js";
import watchdogManifest from "./apps/watchdog/manifest.js";
import franchiseRoadmapManifest from "./apps/franchise-roadmap/manifest.js";
import creatorsManifest from "./apps/creators/manifest.js";
import insightsManifest from "./apps/insights/manifest.js";
import toolsManifest from "./apps/tools/manifest.js";

const ALL_APPS = [
  lrmManifest,
  grandOpeningsManifest,
  vaultManifest,
  socialCalendarManifest,
  franchiseRoadmapManifest,
  creatorsManifest,
  insightsManifest,
  watchdogManifest,
  toolsManifest,
  locationsManifest,
  gameAdminManifest,
  settingsManifest,
];

/* Old admin/super-admin deeplink redirect map. Anything that lived in those
 * apps now lives under settings/<view>. Keep until the muscle memory fades. */
const LEGACY_ROUTE_REDIRECTS = {
  "admin": "settings/team-dashboard",
  "admin/dashboard": "settings/team-dashboard",
  "admin/users": "settings/team-users",
  "super-admin": "settings/platform-flags",
  "super-admin/overview": "settings/platform-flags",
  "super-admin/flags": "settings/platform-flags",
};

/* ─── Global State ────────────────────────────────────────── */

const state = {
  config: null,
  supabase: null,
  session: null,
  profile: null,
  locations: [],
  // Multi-app state
  currentApp: null,       // manifest object or null (home)
  currentView: null,      // view within the app
  availableApps: [],      // filtered by role
};

let appShown = false;
let globalEventsAttached = false;
let openAppGeneration = 0;

/* ─── Boot ────────────────────────────────────────────────── */

document.addEventListener("DOMContentLoaded", boot);

function isPublicPreviewHash() {
  const hash = window.location.hash.replace("#", "");
  return hash.startsWith("public-preview=") || hash.startsWith("public-preview-launch=");
}

async function boot() {
  state.config = getAppConfig();

  // Apply the theme + start watching the OS preference. The inline
  // script in <head> already set data-theme to avoid a flash; this call
  // attaches the matchMedia change listener so users on "system" auto-
  // flip when they toggle macOS dark mode mid-session. Idempotent — we
  // call it again after profile loads to wire DB persistence.
  initTheme();

  // Public-preview URLs (the ones embedded in exported PDFs) carry the
  // entire post payload in the URL fragment — no session required. Skip the
  // auth gate, profile load, and Supabase init entirely so external
  // recipients can view the preview without a login.
  if (isPublicPreviewHash()) {
    bootPublicPreview();
    return;
  }

  if (isSupabaseConfigured(state.config)) {
    try {
      state.supabase = await createSupabaseBrowserClient(state.config);
      const session = await getSupabaseSession(state.supabase);

      if (session) {
        state.session = session;
        await loadProfile();
        hideAuthGate();
        showApp();
      } else {
        showAuthGate();
      }

      subscribeToSupabaseAuth(state.supabase, async (event, newSession) => {
        state.session = newSession;
        if (event === "PASSWORD_RECOVERY") {
          showResetPasswordForm();
          return;
        }
        if (newSession) {
          await loadProfile();
          hideAuthGate();
          showApp();
        } else {
          state.profile = null;
          appShown = false;
          showAuthGate();
        }
      });
    } catch (err) {
      console.error("Supabase init error:", err);
      if (state.config.app.allowLocalFallback) {
        bootLocal();
      } else {
        showAuthGateError("Could not connect to the workspace. Please try again.");
      }
    }
  } else {
    bootLocal();
  }
}

async function bootPublicPreview() {
  // No session, no profile, no Supabase queries, no portal chrome.
  // Load the planner directly — its public-preview short-circuit
  // (inside initPlanner) detects the hash and renders the entire page
  // from the URL payload via document.body.innerHTML replacement.
  hideAuthGate();
  // Hide the splash + auth gate + app shell so nothing flashes.
  document.getElementById("appBootSplash")?.remove();
  document.body.classList.remove("app-booting");
  // Inject the social-calendar CSS the planner relies on.
  if (socialCalendarManifest.css) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = socialCalendarManifest.css;
    document.head.appendChild(link);
  }
  try {
    const { initPlanner } = await import("./apps/social-calendar/engine/planner.js?v=77");
    // Minimal adapter — public preview short-circuit doesn't touch any
    // of these except the container reference.
    const container = document.body;
    const minimalAdapter = {
      supabase: null,
      config: { mode: "local", workspaceName: state.config?.app?.workspaceName || "Angry Chickz", supabaseUrl: "", supabaseAnonKey: "" },
      profile: null,
      session: null,
      showToast,
      root: container,
      refreshTheme: () => {},
    };
    await initPlanner(container, minimalAdapter);
  } catch (err) {
    console.error("Failed to render public preview", err);
    document.body.innerHTML = `
      <main style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:system-ui">
        <div style="text-align:center;max-width:480px">
          <h1 style="font-size:1.6rem;margin-bottom:12px">Preview unavailable</h1>
          <p>This preview link couldn't be loaded. Please ask the sender for a fresh link.</p>
        </div>
      </main>
    `;
  }
}

function bootLocal() {
  state.profile = {
    id: "local",
    email: "local@dev",
    display_name: "Local User",
    role: "admin",
    location_id: null,
  };
  hideAuthGate();
  showApp();
}

/* ─── Profile Loading ─────────────────────────────────────── */

async function loadProfile() {
  if (!state.supabase || !state.session) return;

  const userId = state.session.user.id;
  // Keep this SELECT to columns guaranteed to exist on every deployed
  // schema — adding new columns here makes the whole boot fail in any
  // environment where the migration hasn't run yet (which is how we
  // bricked super_admin login the first time we tried it).
  const { data, error } = await state.supabase
    .from("profiles")
    .select("id, email, display_name, role, location_id, avatar_url")
    .eq("id", userId)
    .single();

  if (error || !data) {
    console.warn("Profile not found, checking invites...");
    await checkInvite();
    return;
  }

  state.profile = data;

  posthog.identify(data.id, { role: data.role });

  // Theme preference fetched separately so the previous SELECT can stay
  // safe across deploys. If the migration hasn't run yet this no-ops
  // gracefully — the user just gets the localStorage / system default
  // until the column lands.
  let dbPreference = null;
  try {
    const { data: themeRow } = await state.supabase
      .from("profiles")
      .select("theme_preference")
      .eq("id", userId)
      .single();
    dbPreference = themeRow?.theme_preference || null;
  } catch {
    // Column missing or any other error — silent. Theme falls back to
    // localStorage cache or system default.
  }

  initTheme({
    supabase: state.supabase,
    profileId: state.profile.id,
    dbPreference,
  });
}

async function checkInvite() {
  if (!state.supabase || !state.session) return;

  // Single-transaction RPC: profile + permissions + locations + districts +
  // invite-status update all happen atomically. See migration
  // 20260429_accept_invite_rpc.sql.
  const { data: profile, error } = await state.supabase.rpc("accept_invite");

  if (error) {
    if (error.code === "P0002" || /no pending invite/i.test(error.message || "")) {
      showAuthGateError("No invitation found for this email. Contact your administrator.");
    } else {
      console.error("accept_invite RPC failed:", error);
      showAuthGateError("Could not complete sign-in. Contact your administrator.");
    }
    await signOutSupabase(state.supabase);
    return;
  }

  state.profile = profile;
}

/* ─── Auth Gate ───────────────────────────────────────────── */

function showAuthGate() {
  const splash = document.getElementById("appBootSplash");
  const gate = document.getElementById("authGate");
  const shell = document.querySelector(".app-shell");

  if (splash) splash.classList.add("hidden");
  if (gate) {
    gate.classList.remove("hidden");
    gate.setAttribute("aria-hidden", "false");
  }
  if (shell) shell.classList.add("hidden");

  attachAuthEvents();
}

function hideAuthGate() {
  const splash = document.getElementById("appBootSplash");
  const gate = document.getElementById("authGate");

  if (splash) splash.classList.add("hidden");
  if (gate) {
    gate.classList.add("hidden");
    gate.setAttribute("aria-hidden", "true");
  }
}

function showAuthGateError(msg) {
  const splash = document.getElementById("appBootSplash");
  if (splash) splash.classList.add("hidden");

  const feedback = document.getElementById("authFeedback");
  if (feedback) {
    feedback.textContent = msg;
    feedback.classList.remove("hidden");
  }
  showAuthGate();
}

function attachAuthEvents() {
  const form = document.getElementById("authForm");
  if (!form || form.dataset.bound) return;
  form.dataset.bound = "true";

  // Password visibility toggle
  const toggleBtn = document.getElementById("authTogglePassword");
  const passwordInput = document.getElementById("authPassword");
  if (toggleBtn && passwordInput) {
    toggleBtn.addEventListener("click", () => {
      const isHidden = passwordInput.type === "password";
      passwordInput.type = isHidden ? "text" : "password";
      toggleBtn.querySelector(".eye-open").classList.toggle("hidden", isHidden);
      toggleBtn.querySelector(".eye-closed").classList.toggle("hidden", !isHidden);
      toggleBtn.setAttribute("aria-label", isHidden ? "Hide password" : "Show password");
    });
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = document.getElementById("authEmail").value.trim();
    const password = document.getElementById("authPassword").value;
    const feedback = document.getElementById("authFeedback");
    const submitBtn = document.getElementById("authSubmitButton");

    if (!email || !password) return;

    submitBtn.disabled = true;
    submitBtn.textContent = "Signing in...";
    feedback.classList.add("hidden");

    try {
      await signInSupabaseWithPassword(state.supabase, email, password);
      posthog.capture("user_signed_in");
    } catch (err) {
      feedback.textContent = err.message || "Sign in failed. Check your credentials.";
      feedback.classList.remove("hidden");
      submitBtn.disabled = false;
      submitBtn.textContent = "Sign in";
    }
  });

  const forgotLink = document.getElementById("showForgotPassword");
  if (forgotLink) {
    forgotLink.addEventListener("click", (e) => {
      e.preventDefault();
      showForgotPasswordForm();
    });
  }

  const backLink = document.getElementById("showSignIn");
  if (backLink) {
    backLink.addEventListener("click", (e) => {
      e.preventDefault();
      showSignInForm();
    });
  }

  const forgotForm = document.getElementById("forgotPasswordForm");
  if (forgotForm && !forgotForm.dataset.bound) {
    forgotForm.dataset.bound = "true";
    forgotForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      const email = document.getElementById("forgotEmail").value.trim();
      const feedback = document.getElementById("authFeedback");
      const submitBtn = document.getElementById("forgotSubmitButton");

      if (!email) return;

      submitBtn.disabled = true;
      submitBtn.textContent = "Sending...";
      feedback.classList.add("hidden");

      try {
        const redirectTo = window.location.origin;
        await resetPasswordForEmail(state.supabase, email, redirectTo);
        posthog.capture("password_reset_requested");
        feedback.textContent = "Check your email for the reset link.";
        feedback.classList.remove("hidden");
        submitBtn.textContent = "Sent!";
      } catch (err) {
        feedback.textContent = err.message || "Could not send reset email.";
        feedback.classList.remove("hidden");
        submitBtn.disabled = false;
        submitBtn.textContent = "Send reset link";
      }
    });
  }

  const resetForm = document.getElementById("resetPasswordForm");
  if (resetForm && !resetForm.dataset.bound) {
    resetForm.dataset.bound = "true";
    resetForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      const newPass = document.getElementById("resetNewPassword").value;
      const confirmPass = document.getElementById("resetConfirmPassword").value;
      const feedback = document.getElementById("authFeedback");
      const submitBtn = document.getElementById("resetSubmitButton");

      if (newPass !== confirmPass) {
        feedback.textContent = "Passwords do not match.";
        feedback.classList.remove("hidden");
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = "Updating...";
      feedback.classList.add("hidden");

      try {
        await updateUserPassword(state.supabase, newPass);
        feedback.textContent = "Password updated! Signing you in...";
        feedback.classList.remove("hidden");
      } catch (err) {
        feedback.textContent = err.message || "Could not update password.";
        feedback.classList.remove("hidden");
        submitBtn.disabled = false;
        submitBtn.textContent = "Set new password";
      }
    });
  }
}

function showForgotPasswordForm() {
  const title = document.getElementById("authGateTitle");
  const message = document.getElementById("authGateMessage");
  if (title) title.textContent = "Reset your password";
  if (message) message.textContent = "Enter your email and we'll send you a reset link.";

  document.getElementById("authForm")?.classList.add("hidden");
  document.getElementById("resetPasswordForm")?.classList.add("hidden");
  document.getElementById("forgotPasswordForm")?.classList.remove("hidden");
  document.getElementById("authFeedback")?.classList.add("hidden");
}

function showSignInForm() {
  const title = document.getElementById("authGateTitle");
  const message = document.getElementById("authGateMessage");
  if (title) title.textContent = "Sign in to continue";
  if (message) message.textContent = "Use the email and password for the account your team created for you.";

  document.getElementById("forgotPasswordForm")?.classList.add("hidden");
  document.getElementById("resetPasswordForm")?.classList.add("hidden");
  document.getElementById("authForm")?.classList.remove("hidden");
  document.getElementById("authFeedback")?.classList.add("hidden");
}

function showResetPasswordForm() {
  const gate = document.getElementById("authGate");
  const splash = document.getElementById("appBootSplash");
  const shell = document.querySelector(".app-shell");

  if (splash) splash.classList.add("hidden");
  if (gate) {
    gate.classList.remove("hidden");
    gate.setAttribute("aria-hidden", "false");
  }
  if (shell) shell.classList.add("hidden");

  const title = document.getElementById("authGateTitle");
  const message = document.getElementById("authGateMessage");
  if (title) title.textContent = "Set a new password";
  if (message) message.textContent = "Enter your new password below.";

  document.getElementById("authForm")?.classList.add("hidden");
  document.getElementById("forgotPasswordForm")?.classList.add("hidden");
  document.getElementById("resetPasswordForm")?.classList.remove("hidden");
  document.getElementById("authFeedback")?.classList.add("hidden");

  attachAuthEvents();
}

/* ─── App Shell ───────────────────────────────────────────── */

async function showApp() {
  if (appShown) return;
  appShown = true;

  const shell = document.querySelector(".app-shell");
  if (shell) shell.classList.remove("hidden");

  // Filter apps by user role + per-user permission overrides
  const role = state.profile?.role || "franchisee";
  let userPerms = [];
  if (state.supabase && state.profile?.id) {
    try {
      const { loadUserPermissions } = await import("./data/crm-data.js");
      userPerms = await loadUserPermissions(state.supabase, state.profile.id);
    } catch (e) { console.warn("Could not load user permissions:", e); }
  }
  state.userPermissions = userPerms;

  state.availableApps = ALL_APPS.filter((app) => {
    const override = userPerms.find((p) => p.app_id === app.id);
    if (override) return override.enabled;
    return app.roles.includes(role);
  });

  updateUserInfo();
  renderImpersonationBanner();
  attachGlobalEvents();

  // Route from hash or show launcher
  handleHashRoute();
}

/* ─── Impersonation Banner ────────────────────────────────── */

function renderImpersonationBanner() {
  // Remove any prior banner — we re-render on every showApp() call.
  document.querySelector(".impersonation-banner")?.remove();

  if (!isImpersonating()) return;

  const target = impersonationTarget();
  const targetLabel = target?.display_name || target?.email || "another user";
  const roleLabel = target?.role ? ` (${target.role.replace("_", " ")})` : "";

  const banner = document.createElement("div");
  banner.className = "impersonation-banner";
  banner.setAttribute("role", "alert");
  banner.innerHTML = `
    <span class="impersonation-banner-text">
      <strong>Impersonating ${escapeHtmlText(targetLabel)}</strong>${escapeHtmlText(roleLabel)}
    </span>
    <button type="button" class="impersonation-banner-stop">Stop impersonating</button>
  `;

  // Insert above .app-main but inside .app-shell so it shifts the rest down.
  const shell = document.querySelector(".app-shell");
  const main = shell?.querySelector(".app-main");
  if (shell && main) shell.insertBefore(banner, main);

  banner.querySelector(".impersonation-banner-stop")?.addEventListener("click", async () => {
    if (!state.supabase) return;
    try { await stopImpersonation(state.supabase); }
    catch (err) { console.error("stopImpersonation failed:", err); }
  });
}

function escapeHtmlText(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function isCorporateOrAdmin() {
  return state.profile && (state.profile.role === "admin" || state.profile.role === "corporate" || state.profile.role === "super_admin");
}

function getAppRole(appId) {
  const profile = state.profile;
  if (!profile) return null;
  if (profile.role === "super_admin") return "admin";
  const perm = (state.userPermissions || []).find((p) => p.app_id === appId);
  if (perm && perm.enabled) return perm.role || "user";
  return profile.role === "admin" ? "admin" : "user";
}

function updateUserInfo() {
  const userName = document.getElementById("currentUserName");
  const userRole = document.getElementById("currentUserRole");

  if (userName) {
    userName.textContent = state.profile?.display_name || "User";
    userName.style.visibility = "visible";
  }
  if (userRole) {
    const roleLabels = {
      super_admin: "Super Admin",
      admin: "Admin",
      corporate: "Corporate",
      franchisee: "Franchisee",
      marketing_agency: "Marketing",
    };
    userRole.textContent = roleLabels[state.profile?.role] || "User";
    userRole.style.visibility = "visible";
  }
}

/* ─── Platform Context (passed to apps) ──────────────────── */

function getPlatformContext() {
  return {
    supabase: state.supabase,
    session: state.session,
    profile: state.profile,
    config: state.config,
    locations: state.locations,
    userPermissions: state.userPermissions || [],
    getAppRole: (appId) => getAppRole(appId),
    openApp: (appId, view, options) => openApp(appId, view, options),
    showToast,
  };
}

/* ─── Navigation ──────────────────────────────────────────── */

function goHome() {
  if (state.currentApp) {
    destroyApp(state.currentApp.id);
  }
  state.currentApp = null;
  state.currentView = null;
  window.location.hash = "";

  const container = document.getElementById("viewContainer");
  const headerNav = document.querySelector(".header-nav");
  const headerTitle = document.querySelector(".header-app-title");
  const eyebrow = document.getElementById("viewEyebrow");

  if (eyebrow) eyebrow.textContent = "";
  renderHeaderHome(headerNav, headerTitle);
  renderLauncher(container, state.availableApps, state.profile);

  // Attach tile click handlers
  container.querySelectorAll("[data-app]").forEach((tile) => {
    tile.addEventListener("click", (e) => {
      e.preventDefault();
      openApp(tile.dataset.app);
    });
  });
}

async function openApp(appId, view, options = {}) {
  const generation = ++openAppGeneration;

  const manifest = state.availableApps.find((a) => a.id === appId);
  if (!manifest) {
    console.warn(`App "${appId}" not found or not available for this role.`);
    goHome();
    return;
  }

  // Destroy previous app (even if same app, to clean up old instance)
  if (state.currentApp) {
    destroyApp(state.currentApp.id);
  }

  state.currentApp = manifest;
  const targetView = view || manifest.defaultView;
  state.currentView = targetView;

  posthog.capture("app_opened", { app_id: appId, app_name: manifest.name });

  // Update URL hash unless caller asked us to leave it alone (e.g. PDF
  // public-preview links carry the post payload in the hash and must be
  // preserved for the planner to read).
  if (!options.preserveHash) {
    const base = view ? `${appId}/${view}` : appId;
    window.location.hash = options.focusPostId ? `${base}/post/${options.focusPostId}` : base;
  }

  // Update header
  const headerNav = document.querySelector(".header-nav");
  const headerTitle = document.querySelector(".header-app-title");
  const eyebrow = document.getElementById("viewEyebrow");

  if (eyebrow) eyebrow.textContent = manifest.name;
  renderHeaderApp(headerNav, headerTitle, manifest, targetView, (newView) => {
    navigateWithinApp(newView);
  });

  // Load and render app
  const container = document.getElementById("viewContainer");
  container.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; padding: var(--space-2xl);">
      <span class="loading-spinner loading-spinner-lg"></span>
    </div>`;

  try {
    await loadApp(manifest, targetView, container, getPlatformContext());
    if (generation !== openAppGeneration) return;
  } catch (err) {
    if (generation !== openAppGeneration) return;
    console.error(`Failed to load app "${appId}":`, err);
    container.innerHTML = `
      <div class="empty-state">
        <h3>Could not load ${manifest.name}</h3>
        <p>${err.message}</p>
      </div>`;
  }
}

async function navigateWithinApp(view) {
  if (!state.currentApp) return;

  state.currentView = view;
  window.location.hash = `${state.currentApp.id}/${view}`;

  const container = document.getElementById("viewContainer");
  try {
    await navigateApp(state.currentApp.id, view, container, getPlatformContext());
  } catch (err) {
    console.error(`Navigation error in ${state.currentApp.id}:`, err);
  }
}

/* ─── Hash Routing ────────────────────────────────────────── */

function handleHashRoute() {
  const hash = window.location.hash.replace("#", "");

  if (!hash) {
    goHome();
    return;
  }

  // PDF exports embed a "public preview" link with the post payload encoded
  // in the hash. The planner inside the social-calendar app handles parsing
  // and rendering — we just need to route there. preserveHash=true so the
  // payload-bearing hash isn't clobbered before the planner reads it.
  if (hash.startsWith("public-preview=") || hash.startsWith("public-preview-launch=")) {
    openApp("social-calendar", null, { preserveHash: true });
    return;
  }

  // Redirect old admin/super-admin deep links to their Settings equivalents.
  const redirect = LEGACY_ROUTE_REDIRECTS[hash];
  if (redirect) {
    window.location.hash = redirect;
    return;  // hashchange will re-fire; we don't double-route here
  }

  const parts = hash.split("/");
  const appId = parts[0];
  const view = parts[1] || null;
  // Optional deep-link tail: #appId/view/post/<id> (e.g. Vault → a specific post)
  const focusPostId = parts[2] === "post" ? (parts[3] || null) : null;

  openApp(appId, view, focusPostId ? { focusPostId } : {});
}

/* ─── Toast ───────────────────────────────────────────────── */

function showToast(message, type = "info") {
  let toastContainer = document.querySelector(".toast-container");
  if (!toastContainer) {
    toastContainer = document.createElement("div");
    toastContainer.className = "toast-container";
    document.body.appendChild(toastContainer);
  }

  const toast = document.createElement("div");
  toast.className = `toast toast--${type}`;
  toast.textContent = message;
  toastContainer.appendChild(toast);

  setTimeout(() => {
    toast.remove();
    if (toastContainer.children.length === 0) {
      toastContainer.remove();
    }
  }, 3000);
}

/* ─── Global Events ───────────────────────────────────────── */

function attachGlobalEvents() {
  if (globalEventsAttached) return;
  globalEventsAttached = true;

  // App switcher / home buttons
  document.querySelectorAll('[data-action="go-home"]').forEach((btn) => {
    btn.addEventListener("click", () => goHome());
  });

  // App switcher button
  const switcherBtn = document.getElementById("appSwitcherBtn");
  if (switcherBtn) {
    switcherBtn.addEventListener("click", () => goHome());
  }

  // Sign out
  const signOutBtn = document.getElementById("signOutButton");
  if (signOutBtn) {
    signOutBtn.addEventListener("click", async () => {
      posthog.capture("user_signed_out");
      posthog.reset();
      if (state.supabase) {
        await signOutSupabase(state.supabase);
      }
      state.session = null;
      state.profile = null;
      window.location.hash = "";
      window.location.reload();
    });
  }

  // Handle browser back/forward
  window.addEventListener("hashchange", () => {
    handleHashRoute();
  });
}

/* ─── Export for modules that need state ──────────────────── */

export { state, isCorporateOrAdmin, getAppRole };
